import { Component, Vue, Watch } from 'vue-property-decorator';
import { SelectOption } from '@/types/types';
import {
  InUseInput,
  PlaceFields,
  PortTypeEnum,
  TransportationTypeEnum
} from '@/types/graphql/schemaTypes';
import FetchPlacesQuery from '@/graphql/queries/places/FetchPlaces.gql';
import i18n from '@/plugins/i18n';

interface PlaceSelectOption extends SelectOption<PlaceFields> {
  normalizeNameAndCountry?: string;
}

@Component({
  apollo: {
    placeOptions: {
      query: FetchPlacesQuery,
      debounce: 300,
      prefetch: true,
      variables() {
        const placeTypes: PortTypeEnum[] = [];

        switch (this.transportationType) {
          case TransportationTypeEnum.sea:
            placeTypes.push(PortTypeEnum.port);
            break;
          case TransportationTypeEnum.air:
            placeTypes.push(PortTypeEnum.airport);
            break;
          case TransportationTypeEnum.any:
          default:
            placeTypes.push(PortTypeEnum.port);
            placeTypes.push(PortTypeEnum.airport);
            break;
        }

        return {
          id: this.searchPlaceId,
          inUse: this.inUse,
          nameSearch: this.searchPlace,
          placeTypes: this.placeTypes || placeTypes
        };
      },
      update(data): PlaceSelectOption[] {
        const options = data.places.map(this.formatPlaceOptions);

        if (!this.placeTypes && this.autoSelect) {
          // legacy
          options.unshift(this.autoSelectOption);
        } else if (
          this.autoSelect &&
          this.placeTypes &&
          !this.placeTypes.includes(PortTypeEnum.bonded_area)
        ) {
          options.unshift(this.autoSelectOption);
        }
        return options;
      }
    }
  }
})
export default class PlaceOptionsMixin extends Vue {
  public autoSelect = true; // add auto select option
  private autoSelectOption = {
    value: { id: null },
    label: i18n.t('ports.place-type.select-label.auto-select').toString()
  };

  public inUse: InUseInput = {
    active: false,
    customerOrganizationId: null
  };

  public placeOptions: PlaceSelectOption[] = [];
  public placeTypes: PortTypeEnum[] | null = null;
  public searchPlace = '';
  public searchPlaceId: string | null = null;
  // NOTE: set transportationType in your component when you use PlaceOptionsMixin
  public transportationType: TransportationTypeEnum =
    TransportationTypeEnum.any;

  public formatPlaceOptions(port: PlaceFields) {
    let code = port.fullCode;
    let i18nKey!: string;

    switch (port.placeType) {
      case PortTypeEnum.airport:
        i18nKey = 'ports.place-type.select-label.airport';
        break;
      case PortTypeEnum.bonded_area:
        code = `${port.ccCodeJp}`.toString();
        i18nKey = 'ports.place-type.select-label.bonded_area';
        break;
      case PortTypeEnum.port:
        i18nKey = 'ports.place-type.select-label.port';
        break;
    }
    return {
      label: i18n.t(i18nKey, { code, name: port.name }).toString(),
      value: port,
      normalizeNameAndCountry: `${port.normalizedName}, ${i18n.t(`countryCode.${port.countryCode}`, 'en').toString()}`
    };
  }

  @Watch('searchPlace')
  private resetSearch() {
    this.searchPlaceId = null;
  }
}
